<template>
  <v-card>
    <v-row class="mx-0 align-center">
      <v-col cols="4">
        <v-select
          v-model="repository"
          label="Repository"
          :items="repositories"
          :loading="isLoading"
          item-text="name"
          item-value="id"
          hide-details
        />
      </v-col>
      <v-col cols="2">
        <v-text-field
          v-model="ref"
          label="Ref"
          :loading="isLoading"
          hide-details
        />
      </v-col>

      <v-col cols="2">
        <v-btn
          color="primary"
          icon
          :loading="isLoading"
          :disabled="!isConfigValid"
          @click="onRunPipeline"
        >
          <v-icon>mdi-play-circle</v-icon>
        </v-btn>
        <v-btn
          icon
          :loading="isLoading"
          color="primary"
          @click="onShowHistory"
        >
          <v-icon>
            mdi-history
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mx-0">
      <v-col
        class="d-flex gap-6 align-center flex-wrap"
        cols="6"
        sty
      >
        <v-chip
          v-for="defaultVariable in availableDefaultVariables"
          :key="defaultVariable"
          :loading="isLoading"
          @click="addVariable(defaultVariable, true)"
        >
          {{ defaultVariable }}
        </v-chip>
        <v-btn
          color="primary"
          :loading="isLoading"
          @click="addVariable()"
        >
          Add Variable
          <v-icon>mdi-plus-circle</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row
      class="mx-0"
      :class="{ 'align-center': !isJobVariable(variable) }"
      v-for="(variable, variableIndex) in variables"
      :key="variable._key"
    >
      <template v-if="isJobVariable(variable) && !variable.value">
        <v-col
          cols="12"
          class="pb-0"
        >
          <v-label v-text="'Select Job'" />
        </v-col>
        <v-col
          v-for="(_, groupKey) in currentRepository.jobList"
          :key="groupKey"
          cols="4"
        >
          <v-radio-group
            v-model="variable.value"
            class="mt-0"
            :label="groupKey"
          >
            <v-radio
              v-for="option in currentRepository.jobList[groupKey]"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </v-radio-group>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="4">
          <v-text-field
            v-model="variable.name"
            :readonly="variable.isReadonly"
            :loading="isLoading"
            :hint="variableHints[variable.name]"
            :hide-details="!variableHints[variable.name]"
            persistent-hint
            label="Variable Name"
          />
        </v-col>
        <v-col cols="4">
          <v-select
            v-if="variable.isReadonly && currentRepository?.jobNameKey === variable.name"
            v-model="variable.value"
            :loading="isLoading"
            :items="jobOptions"
            :hide-details="!variableHints[variable.name]"
            :clearable="isJobVariable(variable)"
            readonly
            label="Select Job"
          />
          <v-text-field
            v-else
            v-model="variable.value"
            :loading="isLoading"
            :hide-details="!variableHints[variable.name]"
            label="Value"
          />
        </v-col>
        <v-col cols="2">
          <v-btn
            v-if="!isJobVariable(variable)"
            color="error"
            icon
            :loading="isLoading"
            @click="variables.splice(variableIndex, 1)"
          >
            <v-icon>
              mdi-close-circle
            </v-icon>
          </v-btn>
        </v-col>
      </template>
    </v-row>
    <v-dialog
      v-model="isShowHistory"
      max-width="1200"
    >
      <v-card
        :loading="isLoading"
      >
        <v-card-title>CI History</v-card-title>
        <v-card-text>
          <v-data-table
            class="history-table"
            :server-items-length="historyTotal"
            :headers="historyHeaders"
            :items="history"
            :loading="isLoading"
            :options.sync="options"
            :footer-props="{'items-per-page-options':[ 20, 50, 100 ]}"
            item-key="pipeline_id"
            show-expand
            single-expand
            @item-expanded="onExpand"
          >
            <template #header.web_url>
              <v-checkbox
                v-model="historyShowMine"
                class="mt-0"
                label="My Launches"
                :loading="isLoading"
                hide-details
              />
            </template>
            <template #item.web_url="{value}">
              <v-btn
                color="primary"
                target="_blank"
                rel="noopener"
                :href="`${value}/builds`"
              >
                URL <v-icon>mdi-link</v-icon>
              </v-btn>
            </template>
            <template #item.variables="{value}">
              <v-row
                v-for="(_, variable) in value"
                :key="variable"
              >
                <span class="d-inline-block text-truncate" v-text="variable" />=
                <v-tooltip top>
                  <template #activator="{on}">
                    <span
                      v-text="value[variable]"
                      v-on="on"
                      class="d-inline-block text-truncate"
                      style="max-width: 5rem; cursor: pointer;"
                      @click="copyVar(value[variable])"
                    />
                  </template>
                  <span>Click to copy value</span>
                </v-tooltip>
              </v-row>
            </template>

            <template #expanded-item="{ item }">
              <td :colspan="historyHeaders.length + 1" class="pa-0">
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>
                        Job name
                      </th>
                      <th>
                        Created At
                      </th>
                      <th>
                        Finished At
                      </th>
                      <th>
                        Status
                      </th>
                      <th>
                        Trace
                      </th>
                      <th>
                        Artifacts
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="job in pipelineJobs"
                      :key="job.id"
                    >
                      <td v-text="job.name" />
                      <td v-text="job.created_at" />
                      <td v-text="job.finished_at" />
                      <td v-text="job.status" />
                      <td>
                        <v-btn
                          icon
                          color="primary"
                          @click="$emit('download-artifacts', { pipeline_id: item.pipeline_id, job_id: job.id, type: 'trace' })"
                        >
                          <v-icon>
                            mdi-download-circle-outline
                          </v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-btn
                          v-if="job.artifacts.length"
                          icon
                          color="primary"
                          @click="$emit('download-artifacts', { pipeline_id: item.pipeline_id, job_id: job.id, type: 'artifacts' })"
                        >
                          <v-icon>
                            mdi-download-circle-outline
                          </v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <v-progress-linear
                  v-if="download.isInProgress"
                  height="25"
                  :value="download.progress / download.total * 100"
                >
                  Downloading {{ download.type }}
                </v-progress-linear>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { difference, map } from 'lodash'

export default {
  props: {
    repositories: {
      type: Array,
      required: true,
    },
    variableHints: {
      type: Object,
      required: true,
    },
    history: {
      type: Array,
      required: true,
    },
    pipelineJobs: {
      type: Array,
      required: true,
    },
    historyTotal: Number,
    isLoading: Boolean,
    download: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      repository: null,
      ref: 'master',
      variables: [],
      isShowHistory: false,
      historyShowMine: true,
      historyHeaders: [
        { text: 'Pipeline ID', value: 'pipeline_id', },
        { text: 'Created', value: 'created', },
        { text: 'User', value: 'user', },
        { text: 'Variables', value: 'variables', cellClass: 'variables-json' },
        { text: '', value: 'web_url', sortable: false, },
      ],
      options: {},
    }
  },
  computed: {
    variableSuggestions() {
      if (!this.currentRepository) {
        return {}
      }
      const suggestions = {}

      for (const groupKey in this.currentRepository.jobList) {
        for (const job of this.currentRepository.jobList[groupKey]) {
          if (Array.isArray(job.variables)) {
            suggestions[job.value] = [...job.variables]
          }
        }
      }

      return suggestions
    },

    selectedJob() {
      if (!this.currentRepository) {
        return null
      }
      const jobVarName = this.currentRepository.jobNameKey
      const jobVar = this.variables.find(variable => variable.name === jobVarName)

      if (!jobVar) {
        return null
      }

      return jobVar.value
    },

    availableDefaultVariables() {
      if (!this.selectedJob) {
        return []
      }

      return difference(this.variableSuggestions[this.selectedJob], map(this.variables, 'name'))
    },

    isConfigValid() {
      return !!this.repository &&
             !!this.ref &&
             this.variables.every(variable => !!variable.name && !!variable.value) &&
             (new Set(map(this.variables, 'name')).size === this.variables.length)
    },

    currentRepository() {
      return this.repositories.find(repository => repository.id === this.repository)
    },

    jobOptions() {
      if (!this.currentRepository) {
        return []
      }
      const options = []
      for (const groupKey in this.currentRepository.jobList) {
        options.push({ header: groupKey })

        for (const job of this.currentRepository.jobList[groupKey]) {
          options.push({text: job.label, value: job.value})
        }
      }
      return options
    }
  },
  methods: {
    addVariable(name = '', isReadonly = false) {
      this.variables.push({
        name,
        value: '',
        isReadonly,
        _key: isReadonly ? `${name}:${isReadonly}` : `${Math.random()}:${this.variables.length}`,
      })
    },

    getVariableBreakdown() {
      return this.variables.map(variable => `${variable.name}=${variable.value}`).join(', ')
    },

    onRunPipeline() {
      const variableBreakdown = this.getVariableBreakdown()
      this.$root.$confirm('Run pipeline?', variableBreakdown ? `Run with the following params ${variableBreakdown}` : 'No params').then(yes => {
        if (!yes) {
          return
        }

        const variables = {}
        for (const variable of this.variables) {
          variables[variable.name] = variable.value
        }

        this.$emit('run-pipeline', {
          repository: this.repository,
          ref: this.ref,
          variables,
        })
      })
    },

    onShowHistory() {
      this.showHistoryWithParams()
      this.isShowHistory = true
    },

    showHistoryWithParams() {
      this.$emit('show-history', { showMine: this.historyShowMine, ...this.options })
    },

    formatHistoryVariables(variables) {
      return Object.keys(variables).map(key => `${key}=${variables[key]}`).join('\n')
    },

    isJobVariable(variable) {
      return variable.isReadonly && this.currentRepository?.jobNameKey === variable.name
    },

    onExpand(args) {
      if (args.value) {
        this.$emit('get-pipeline-info', { pipeline_id: args.item.pipeline_id })
      }
    },

    copyVar(text) {
      navigator.clipboard.writeText(text)
        .then(() => {
          this.$showSuccess('Copied successfully')
        })
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.showHistoryWithParams()
      }
    },
    historyShowMine() {
      this.showHistoryWithParams()
    },
    repository(repository) {
      if (repository) {
        const jobVarName = this.currentRepository.jobNameKey

        if (!this.variables.find(variable => variable.name === jobVarName)) {
          this.variables.unshift({
            name: jobVarName,
            value: '',
            isReadonly: true,
            _key: `${Math.random()}:${this.variables.length}`,
          })
        }
      }
    }
  },
  emits: ['run-pipeline', 'show-history', 'get-pipeline-info', 'download-artifacts'],
}
</script>

<style lang="scss" scoped>
.gap-6 {
  gap: 0.5rem;
}

::v-deep {
  .variables-json {
    white-space: pre-line;
  }
  .v-data-table__expanded__content {
    box-shadow: none !important;
  }
}
</style>
